import { useEffect, useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { DataTable } from "../../components/ui/data-table";
import { Voucher } from "../../types/voucher";
import { useGetVouchers, useGetVoucherStatusCount } from "../../api/voucher";
import {
  getStatusText,
  getVoucherStatusConfig,
} from "../../util/status-formatter";
import { useNavigate } from "react-router-dom";
import { formatDateToISO } from "../../util/date-formatter";
import { Icons } from "../../assets/images/icons";
import { formatNumber } from "../../util/price-formatter";
import { FilterTabs } from "../../components/ui/FilterTabs";
import { Input } from "../../components/ui/input";
import { AppButton } from "../../components/ui/AppButton";
import { ExportButton } from "../../components/ui/ExportButton";
import { useTablePrint } from "../../hooks/useTablePrint";

const VoucherPage = () => {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [countAll, setCountAll] = useState<number>(0);

  /** API */
  const { data: vouchers, isLoading } = useGetVouchers({
    status: activeFilter === "all" ? undefined : activeFilter.toUpperCase(),
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });
  const { handlePrint } = useTablePrint();

  const { data: statuses } = useGetVoucherStatusCount();

  /** Transform vouchers for table */
  const transformedVouchers = useMemo((): Voucher[] => {
    if (!vouchers?.data?.content) return [];
    return vouchers.data?.content.map((item: Voucher) => ({
      id: item.id,
      voucherCode: item.voucherCode,
      voucherPrice: formatNumber(item.inputPayed) || "N/A",
      issuedDate: item.issuanceDate,
      issuedBy: item.issuedBy.fullName,
      expiryDate: item.expiryDate,
      status: item.voucherStatus,
    }));
  }, [vouchers]);

  /** Filter counts */
  const filterCounts = useMemo(() => {
    const statusData = statuses?.data?.data;
    return {
      all: vouchers?.data?.totalElements || 0,
      issued: statusData?.issued || 0,
      redeemed: statusData?.redeemed || 0,
      partially_redeemed: statusData?.partially_redeemed || 0,
    };
  }, [statuses, vouchers]);

  const filterTabs = [
    { value: "all", label: "All", count: countAll },
    { value: "redeemed", label: "Redeemed", count: filterCounts.redeemed },
    { value: "issued", label: "Issued", count: filterCounts.issued },
    {
      value: "partially_redeemed",
      label: "Partially Redeemed",
      count: filterCounts.partially_redeemed,
    },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleRowClick = (voucher: Voucher) =>
    navigate(`/voucher/${voucher.id}`);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (activeFilter === "all") setCountAll(vouchers?.data?.totalElements || 0);
  }, [activeFilter, vouchers?.data?.totalElements]);

  /** Columns */
  const voucherColumns = [
    {
      key: "voucherCode",
      title: "Voucher Code",
      dataIndex: "voucherCode",
      align: "left" as const,
    },
    {
      key: "voucherPrice",
      title: "Voucher Price (ETB)",
      dataIndex: "voucherPrice",
      align: "right" as const,
    },
    {
      key: "issuedDate",
      title: "Issued Date",
      dataIndex: "issuedDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "issuedBy",
      title: "Issued By",
      dataIndex: "issuedBy",
      align: "left" as const,
    },
    {
      key: "expiryDate",
      title: "Expiry Date",
      dataIndex: "expiryDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: number) => (
        <div
          className={`
            inline-flex items-center justify-center min-w-[100px] max-w-[140px] px-3 py-1.5 rounded-full text-xs font-normal transition-all duration-200
            ${getVoucherStatusConfig(value).className}
          `}
        >
          {getStatusText(value)}
        </div>
      ),
    },
  ];

  return (
    <div
      className="bg-transparent lg:bg-white"
      style={{ borderRadius: 8.5, marginTop: 25 }}
    >
      {/** Header Filters */}
      <div className="space-y-4 mb-6">
        <FilterTabs
          tabs={filterTabs}
          activeTab={activeFilter}
          onTabChange={handleFilterChange}
          className="w-full lg:w-auto"
        />
      </div>

      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search vouchers"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(voucherColumns, transformedVouchers, "Voucher Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={voucherColumns}
            data={transformedVouchers}
            fileName="vouchers"
            title="Voucher Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: vouchers?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      <div className="hidden md:block">
        <DataTable<Voucher>
          data={transformedVouchers}
          columns={voucherColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: vouchers?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No vouchers found"
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default WithAuth(VoucherPage);
